import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import type { Acces } from '@/shared/auth/types';
import { cn } from '@/shared/ui';
import { rechercher, type ResultatRecherche } from './recherche';

/**
 * La recherche globale, ouverte par Ctrl K (⌘ K sur Mac) ou le champ de l'en-tête.
 *
 * Flèches pour choisir, Entrée pour ouvrir, Échap pour fermer.
 */
export function PaletteRecherche({
  role,
  ceo,
  onFermer,
}: {
  role: Acces;
  ceo: boolean;
  onFermer: () => void;
}) {
  const navigate = useNavigate();
  const [requete, setRequete] = useState('');
  const [resultats, setResultats] = useState<ResultatRecherche[]>([]);
  const [actif, setActif] = useState(0);
  /* La saisie à laquelle répondent les résultats affichés : la recherche est asynchrone. */
  const resultatsPour = useRef<string | null>(null);
  const champ = useRef<HTMLInputElement>(null);
  const idListe = useId();

  useEffect(() => {
    champ.current?.focus();
  }, []);

  useEffect(() => {
    let courant = true;
    void rechercher(requete, role, ceo).then((trouves) => {
      if (!courant) return;
      resultatsPour.current = requete;
      setResultats(trouves);
      setActif(0);
    });
    return () => {
      courant = false;
    };
  }, [requete, role, ceo]);

  function ouvrir(resultat: ResultatRecherche | undefined) {
    if (!resultat) return;
    onFermer();
    void navigate(resultat.chemin);
  }

  function auClavier(evenement: React.KeyboardEvent) {
    if (evenement.key === 'Escape') {
      evenement.preventDefault();
      onFermer();
    } else if (evenement.key === 'ArrowDown') {
      evenement.preventDefault();
      setActif((i) => Math.min(i + 1, resultats.length - 1));
    } else if (evenement.key === 'ArrowUp') {
      evenement.preventDefault();
      setActif((i) => Math.max(i - 1, 0));
    } else if (evenement.key === 'Enter') {
      evenement.preventDefault();
      /* Entrée frappée avant la réponse à la dernière saisie : le premier résultat de cette saisie, pas un ancien. */
      if (resultatsPour.current === requete) ouvrir(resultats[actif]);
      else
        void rechercher(requete, role, ceo).then((trouves) => {
          ouvrir(trouves[0]);
        });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-nav/40 px-4 pt-[12vh]"
      onMouseDown={onFermer}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Recherche"
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={auClavier}
      >
        <div className="flex items-center gap-3 border-b border-border-subtle px-4">
          <Search className="size-[18px] text-faint" aria-hidden />
          <input
            ref={champ}
            value={requete}
            onChange={(e) => {
              setRequete(e.target.value);
            }}
            placeholder="Rechercher un écran, un chantier, un partenaire…"
            className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-faint"
            role="combobox"
            aria-expanded="true"
            aria-controls={idListe}
            aria-activedescendant={
              resultats[actif] ? `${idListe}-${resultats[actif].id}` : undefined
            }
          />
          <kbd className="rounded-md border border-border bg-surface-sunken px-1.5 text-[11px] text-muted">
            Échap
          </kbd>
        </div>

        <ul id={idListe} role="listbox" className="max-h-80 overflow-y-auto p-2">
          {resultats.length === 0 ? (
            <li className="px-3 py-8 text-center text-[13px] text-muted">
              Rien ne correspond à « {requete} ».
            </li>
          ) : (
            resultats.map((resultat, index) => {
              const Icone = resultat.icone;
              return (
                <li
                  key={resultat.id}
                  id={`${idListe}-${resultat.id}`}
                  role="option"
                  aria-selected={index === actif}
                  onMouseEnter={() => {
                    setActif(index);
                  }}
                  onClick={() => {
                    ouvrir(resultat);
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5',
                    index === actif && 'bg-primary-subtle'
                  )}
                >
                  <span className="grid size-8 place-items-center rounded-lg bg-primary-subtle text-primary">
                    <Icone className="size-4" aria-hidden />
                  </span>
                  <span className="flex-1">
                    <span className="block text-[13px] font-medium">{resultat.libelle}</span>
                    <span className="block text-xs text-muted">{resultat.detail}</span>
                  </span>
                  {index === actif ? (
                    <CornerDownLeft className="size-4 text-primary" aria-hidden />
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
        <div className="border-t border-border-subtle px-4 py-2 text-[11px] text-faint">
          Écrans, chantiers (référence, commune), entreprises et partenaires.
        </div>
      </div>
    </div>
  );
}
